import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import TransformControls from './TransformControls';
import ModelLoader from './ModelLoader';
import * as THREE from 'three';

// Default positions for different clothing categories
const CATEGORY_POSITIONS = {
  default: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
  tops: { position: [0, 1.2, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
  bottoms: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
  shoes: { position: [0, 0.3, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
  accessories: { position: [0, 1.0, 0], rotation: [0, 0, 0], scale: [0.8, 0.8, 0.8] }
};

export default function ModelManager({ 
  loadedModels, 
  onModelLoad, 
  onModelError,
  selectedModelId,
  onModelSelect,
  transformMode = 'translate',
  isSnappingEnabled = false,
  constraints = { x: true, y: true, z: true },
  onSelectedObjectChange,
  onTransformChange,
  scaleLimits = { min: 0.1, max: 3.0 },
  positionLimits = { x: { min: -5, max: 5 }, y: { min: -3, max: 5 }, z: { min: -5, max: 5 } },
  orbitControlsRef
}) {
  const [activeModels, setActiveModels] = useState([]);
  const [modelObjects, setModelObjects] = useState(new Map());
  const [modelTransforms, setModelTransforms] = useState(new Map());
  const [modelInitialTransforms, setModelInitialTransforms] = useState(new Map());
  const [selectedObject, setSelectedObject] = useState(null);
  const [currentTransformValues, setCurrentTransformValues] = useState(null);

  const { camera, scene } = useThree();
  const groupRef = useRef();

  // Update current transform values for the selected object
  const updateCurrentTransformValues = useCallback(() => {
    if (selectedObject) {
      const newValues = {
        position: {
          x: Math.round(selectedObject.position.x * 1000) / 1000,
          y: Math.round(selectedObject.position.y * 1000) / 1000,
          z: Math.round(selectedObject.position.z * 1000) / 1000
        },
        rotation: {
          x: Math.round(selectedObject.rotation.x * 1000) / 1000,
          y: Math.round(selectedObject.rotation.y * 1000) / 1000,
          z: Math.round(selectedObject.rotation.z * 1000) / 1000
        },
        scale: {
          x: Math.round(selectedObject.scale.x * 1000) / 1000,
          y: Math.round(selectedObject.scale.y * 1000) / 1000,
          z: Math.round(selectedObject.scale.z * 1000) / 1000
        }
      };
      
      setCurrentTransformValues(newValues);
      
      // Notify parent about the transform update
      if (onTransformChange) {
        onTransformChange({
          position: [newValues.position.x, newValues.position.y, newValues.position.z],
          rotation: [newValues.rotation.x, newValues.rotation.y, newValues.rotation.z],
          scale: [newValues.scale.x, newValues.scale.y, newValues.scale.z],
          transformUpdated: true,
          currentValues: newValues
        });
      }
    }
  }, [selectedObject, onTransformChange]);

  // Update active models when loadedModels changes
  useEffect(() => {
    if (loadedModels && Array.isArray(loadedModels)) {
      setActiveModels(loadedModels);
      
      // Initialize modelTransforms with saved transform data from loaded models
      const initialTransforms = new Map();
      loadedModels.forEach(model => {
        if (model.currentTransform) {
          console.log(`[ModelManager] Initializing saved transform for ${model.id}:`, model.currentTransform);
          initialTransforms.set(model.id, {
            position: model.currentTransform.position || [0, 0, 0],
            rotation: model.currentTransform.rotation || [0, 0, 0],
            scale: model.currentTransform.scale || [1, 1, 1]
          });
        }
      });
      
      if (initialTransforms.size > 0) {
        console.log(`[ModelManager] Initialized ${initialTransforms.size} saved transforms`);
        setModelTransforms(initialTransforms);
      }
    }
  }, [loadedModels]);

  // Clear selected object when models are cleared or selection becomes invalid
  useEffect(() => {
    if (loadedModels.length === 0 || !selectedModelId) {
      console.log(`[ModelManager] Clearing selected object - no models or no selection`);
      setSelectedObject(null);
      // Also clear transform data when clearing the stage
      setModelTransforms(new Map());
      // Clear model objects references
      setModelObjects(new Map());
      
      // CRITICAL: Ensure selection is cleared in parent when stage is empty
      if (loadedModels.length === 0 && onModelSelect) {
        console.log(`[ModelManager] Stage cleared - notifying parent to clear selection`);
        onModelSelect(null);
      }
    }
  }, [loadedModels, selectedModelId, onModelSelect]);

  // Handle model selection
  const handleModelSelect = useCallback((modelId, object) => {
    console.log(`[ModelManager] Model selected: ${modelId}`, object);
    setSelectedObject(object);
    if (onModelSelect) {
      onModelSelect(modelId);
    }
  }, [onModelSelect]);

  // Handle background double-click to clear selection
  const handleBackgroundDoubleClick = useCallback((event) => {
    // Check if we double-clicked on the scene background, group, background plane, or any non-model object
    if (event.object === scene || 
        event.object === groupRef.current || 
        event.object.userData?.isBackground ||
        !event.object.userData?.modelId) {
      console.log(`[ModelManager] Background double-clicked, clearing selection`);
      setSelectedObject(null);
      if (onModelSelect) {
        onModelSelect(null);
      }
    }
  }, [scene, onModelSelect]);

  // Handle transform changes - CRITICAL: This is where transforms are persisted
  const handleTransformChange = useCallback((transformData) => {
    if (selectedObject && onTransformChange) {
      const modelId = selectedObject.userData.modelId;
      if (modelId) {
        console.log(`[ModelManager] Transform changed for ${modelId}:`, transformData);
        
        // Store the transform in our local state
        setModelTransforms(prev => new Map(prev).set(modelId, {
          position: transformData.position,
          rotation: transformData.rotation,
          scale: transformData.scale,
          timestamp: Date.now()
        }));
        
        // Update current transform values
        updateCurrentTransformValues();
      }
    }
  }, [selectedObject, onTransformChange, updateCurrentTransformValues]);

  // Direct transform update for numeric panel
  const handleDirectTransformUpdate = useCallback((transformData) => {
    if (selectedObject) {
      console.log(`[ModelManager] Direct transform update:`, transformData);
      
      // Apply transform directly to the 3D object
      if (transformData.position) {
        selectedObject.position.set(...transformData.position);
      }
      if (transformData.rotation) {
        selectedObject.rotation.set(...transformData.rotation);
      }
      if (transformData.scale) {
        selectedObject.scale.set(...transformData.scale);
      }
      
      // Update the object's matrix
      selectedObject.updateMatrixWorld();
      
      // Store the transform in our local state
      const modelId = selectedObject.userData.modelId;
      if (modelId) {
        setModelTransforms(prev => new Map(prev).set(modelId, {
          position: transformData.position || [selectedObject.position.x, selectedObject.position.y, selectedObject.position.z],
          rotation: transformData.rotation || [selectedObject.rotation.x, selectedObject.rotation.y, selectedObject.rotation.z],
          scale: transformData.scale || [selectedObject.scale.x, selectedObject.scale.y, selectedObject.scale.z],
          timestamp: Date.now()
        }));
      }
      
      // Notify parent about the transform update
      if (onTransformChange) {
        onTransformChange({
          ...transformData,
          transformUpdated: true
        });
      }
    }
  }, [selectedObject, onTransformChange]);

  // Handle transform start
  const handleTransformStart = useCallback(() => {
    console.log(`[ModelManager] Transform started`);
    
    // CRITICAL: Store camera state at the start of drag
    if (camera && selectedObject) {
      selectedObject.userData.lastCameraPosition = camera.position.clone();
      selectedObject.userData.lastCameraRotation = camera.rotation.clone();
      console.log(`[ModelManager] Camera state stored at drag start:`, {
        position: selectedObject.userData.lastCameraPosition,
        rotation: selectedObject.userData.lastCameraRotation
      });
    }
    // Note: Orbit controls are now handled by TransformControls component
  }, [camera, selectedObject]);

  // Handle transform end
  const handleTransformEnd = useCallback(() => {
    console.log(`[ModelManager] Transform ended`);
    // Note: Orbit controls are now handled by TransformControls component
  }, []);

  // Store model objects for selection
  const handleModelObject = useCallback((modelId, object) => {
    setModelObjects(prev => new Map(prev).set(modelId, object));
  }, []);

  // Auto-frame camera to selected object - ONLY when selection changes, not every frame
  useEffect(() => {
    if (selectedObject && camera) {
      console.log(`[ModelManager] Auto-framing camera to selected object`);
      
      // CRITICAL: Check if we're currently dragging - don't move camera during drag
      const isCurrentlyDragging = selectedObject.userData?.transformProtected === true;
      if (isCurrentlyDragging) {
        console.warn(`[ModelManager] WARNING: Attempted to auto-frame camera during drag! Skipping.`);
        return;
      }
      
      const box = new THREE.Box3().setFromObject(selectedObject);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      
      // Calculate distance based on object size
      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 2;
      
      // Position camera to frame the object
      const direction = new THREE.Vector3(1, 0.5, 1).normalize();
      camera.position.copy(center).add(direction.multiplyScalar(distance));
      camera.lookAt(center);
      camera.updateMatrixWorld();
      
      // Update orbit controls target if they exist
      const orbitControls = scene.children.find(child => child.type === 'OrbitControls');
      if (orbitControls && orbitControls.target) {
        orbitControls.target.copy(center);
        orbitControls.update();
        console.log(`[ModelManager] Updated orbit target to:`, center);
      }
    }
  }, [selectedObject, camera, scene]);

  // CRITICAL: Remove any frame-loop updates that could interfere with transforms
  useFrame((state, delta) => {
    // DO NOT update transforms here - let TransformControls handle everything
    // This prevents the "snap back" issue
    
    // CRITICAL: Prevent any camera movement during drag operations - but optimize for performance
    if (selectedObject && selectedObject.userData?.transformProtected === true) {
      // Use frame counter to reduce performance impact
      if (!selectedObject.userData.modelManagerFrameCounter) {
        selectedObject.userData.modelManagerFrameCounter = 0;
      }
      selectedObject.userData.modelManagerFrameCounter++;
      
      // Only check every 30 frames to reduce lag even more
      if (selectedObject.userData.modelManagerFrameCounter % 30 === 0) {
        // Note: Orbit controls are now properly handled by TransformControls
        // This is just a failsafe to ensure camera doesn't move during transforms
        
        // Much less aggressive camera movement prevention
        if (camera && selectedObject.userData.lastCameraPosition) {
          const currentPosition = camera.position;
          const lastPosition = selectedObject.userData.lastCameraPosition;
          
          // Use direct comparison instead of cloning for better performance
          if (Math.abs(currentPosition.x - lastPosition.x) > 0.001 ||
              Math.abs(currentPosition.y - lastPosition.y) > 0.001 ||
              Math.abs(currentPosition.z - lastPosition.z) > 0.001) {
            currentPosition.copy(lastPosition);
            camera.updateMatrixWorld();
          }
        }
        
        if (camera && selectedObject.userData.lastCameraRotation) {
          const currentRotation = camera.rotation;
          const lastRotation = selectedObject.userData.lastCameraRotation;
          
          // Use direct comparison instead of cloning for better performance
          if (Math.abs(currentRotation.x - lastRotation.x) > 0.001 ||
              Math.abs(currentRotation.y - lastRotation.y) > 0.001 ||
              Math.abs(currentRotation.z - lastRotation.z) > 0.001) {
            currentRotation.copy(lastRotation);
            camera.updateMatrixWorld();
          }
        }
      }
    } else {
      // Reset frame counter when not dragging
      if (selectedObject) {
        selectedObject.userData.modelManagerFrameCounter = 0;
      }
    }
  });

  // Handle model load to store initial transforms
  const handleModelLoad = useCallback((modelId, scene, metadata) => {
    console.log(`[ModelManager] Model loaded: ${modelId}`, metadata);
    
    // Store initial transform data for this model
    if (metadata && metadata.initialTransform) {
      console.log(`[ModelManager] Storing initial transform for ${modelId}:`, metadata.initialTransform);
      
      // Convert array format to object format for easier use
      const initialTransform = {
        position: {
          x: metadata.initialTransform.position[0] || 0,
          y: metadata.initialTransform.position[1] || 0,
          z: metadata.initialTransform.position[2] || 0
        },
        rotation: {
          x: metadata.initialTransform.rotation[0] || 0,
          y: metadata.initialTransform.rotation[1] || 0,
          z: metadata.initialTransform.rotation[2] || 0
        },
        scale: {
          x: metadata.initialTransform.scale[0] || 1,
          y: metadata.initialTransform.scale[1] || 1,
          z: metadata.initialTransform.scale[2] || 1
        }
      };
      
      // Store in our local state
      setModelInitialTransforms(prev => new Map(prev).set(modelId, initialTransform));
      
      console.log(`[ModelManager] Initial transform stored for ${modelId}:`, initialTransform);
    }
    
    if (onModelLoad) {
      onModelLoad(modelId, scene, metadata);
    }
  }, [onModelLoad]);

  const handleModelError = useCallback((modelId, error) => {
    console.error(`[ModelManager] Error loading model ${modelId}:`, error);
    if (onModelError) {
      onModelError(modelId, error);
    }
  }, [onModelError]);

  // Memoize the model components to prevent recreation
  const modelComponents = useMemo(() => {
    return activeModels.map((model) => {
      let category = model.category || 'tops';
      
      // Map old accessory categories to the new unified "accessories" category
      if (['hats', 'necklaces', 'belts', 'watches'].includes(category)) {
        category = 'accessories';
      }
      
      const config = CATEGORY_POSITIONS[category] || CATEGORY_POSITIONS.default;
      const isSelected = selectedModelId === model.id;
      
      // Get the current transform from our state, or use the saved transform, or fall back to initial config
      let currentTransform;
      
      if (modelTransforms.has(model.id)) {
        // Use the current transform from our state (for active editing)
        currentTransform = modelTransforms.get(model.id);
        console.log(`[ModelManager] Using active transform for ${model.id}:`, currentTransform);
      } else if (model.currentTransform) {
        // Use the saved transform from the outfit
        currentTransform = {
          position: model.currentTransform.position || config.position,
          rotation: model.currentTransform.rotation || config.rotation,
          scale: model.currentTransform.scale || config.scale
        };
        console.log(`[ModelManager] Using saved transform for ${model.id}:`, currentTransform);
      } else {
        // Fall back to default category positioning
        currentTransform = {
          position: model.position || config.position,
          rotation: model.rotation || config.rotation,
          scale: model.scale || config.scale
        };
        console.log(`[ModelManager] Using default transform for ${model.id}:`, currentTransform);
      }
      
      console.log(`[ModelManager] Rendering model ${model.id} with transform:`, currentTransform);
      console.log(`[ModelManager] Model data:`, model);
      
      return (
        <ModelLoader
          key={model.id} // Stable key prevents recreation
          modelId={model.id}
          modelUrl={model.modelUrl}
          position={currentTransform.position}
          rotation={currentTransform.rotation}
          scale={currentTransform.scale}
          onLoad={(scene, metadata) => handleModelLoad(model.id, scene, metadata)}
          onError={(error) => handleModelError(model.id, error)}
          onSelect={handleModelSelect}
          isSelected={isSelected}
        />
      );
    });
  }, [activeModels, selectedModelId, modelTransforms, handleModelLoad, handleModelError, handleModelSelect]);

  // Use useFrame to continuously update transform values during transforms
  useFrame(() => {
    if (selectedObject) {
      // Check if the object has moved since last update
      const currentPos = {
        x: Math.round(selectedObject.position.x * 1000) / 1000,
        y: Math.round(selectedObject.position.y * 1000) / 1000,
        z: Math.round(selectedObject.position.z * 1000) / 1000
      };
      const currentRot = {
        x: Math.round(selectedObject.rotation.x * 1000) / 1000,
        y: Math.round(selectedObject.rotation.y * 1000) / 1000,
        z: Math.round(selectedObject.rotation.z * 1000) / 1000
      };
      const currentScl = {
        x: Math.round(selectedObject.scale.x * 1000) / 1000,
        y: Math.round(selectedObject.scale.y * 1000) / 1000,
        z: Math.round(selectedObject.scale.z * 1000) / 1000
      };

      // Only update if values have actually changed
      if (
        !currentTransformValues ||
        JSON.stringify(currentTransformValues.position) !== JSON.stringify(currentPos) ||
        JSON.stringify(currentTransformValues.rotation) !== JSON.stringify(currentRot) ||
        JSON.stringify(currentTransformValues.scale) !== JSON.stringify(currentScl)
      ) {
        const newValues = {
          position: currentPos,
          rotation: currentRot,
          scale: currentScl
        };
        
        setCurrentTransformValues(newValues);
        
        // Notify parent about the transform update
        if (onTransformChange) {
          onTransformChange({
            position: [currentPos.x, currentPos.y, currentPos.z],
            rotation: [currentRot.x, currentRot.y, currentRot.z],
            scale: [currentScl.x, currentScl.y, currentScl.z],
            transformUpdated: true,
            currentValues: newValues
          });
        }
      }
    }
  });

  // Notify parent when selected object changes
  useEffect(() => {
    if (onSelectedObjectChange) {
      onSelectedObjectChange(selectedObject);
    }
    
    // Update transform values when selection changes
    if (selectedObject) {
      updateCurrentTransformValues();
    }
  }, [selectedObject, onSelectedObjectChange, updateCurrentTransformValues]);

  // Get initial transform for a specific model
  const getInitialTransform = useCallback((modelId) => {
    return modelInitialTransforms.get(modelId);
  }, [modelInitialTransforms]);

  return (
    <group ref={groupRef} onDoubleClick={handleBackgroundDoubleClick}>
      {modelComponents}
      
      {/* Transform Controls */}
      {selectedObject && (
        <TransformControls
          selectedObject={selectedObject}
          mode={transformMode}
          onTransformChange={handleTransformChange}
          onTransformStart={handleTransformStart}
          onTransformEnd={handleTransformEnd}
          isSnappingEnabled={isSnappingEnabled}
          constraints={constraints}
          scaleLimits={scaleLimits}
          positionLimits={positionLimits}
          orbitControlsRef={orbitControlsRef}
        />
      )}
    </group>
  );
}
